import React from "react";
import Navbar from "./navbar";
import ServiceHeroSection from "./ServiceHeroSection";
import Footer from "./Footer";
import StatsCounter from "./StatsCounter";
import { Globe, Database, Server, Code2, Layers, Zap, Shield, CheckCircle } from "lucide-react";
import WebServiceCoreTech from "./WebServiceCoreTech";
import WebServiceCloudAndIntegrations from "./WebServiceCloudAndIntegrations";
import WebServiceEnterpriseQuality from "./WebServiceEnterpriseQuality";
import CallToAction from "./CallToAction";

const WebUI = () => (
  <div className="relative flex items-center justify-center w-full h-full py-10">
    {/* Glow */}
    <div className="absolute w-72 h-72 rounded-full bg-blue-600/20 blur-3xl" />

    {/* Browser Window */}
    <div className="relative z-10 w-[340px] rounded-2xl border border-white/20 bg-white/5 backdrop-blur-xl shadow-[0_0_60px_rgba(37,99,235,.35)] overflow-hidden">
      {/* Browser Bar */}
      <div className="flex items-center gap-2 px-4 py-3 bg-white/10 border-b border-white/10">
        <div className="w-3 h-3 rounded-full bg-red-400" />
        <div className="w-3 h-3 rounded-full bg-yellow-400" />
        <div className="w-3 h-3 rounded-full bg-green-400" />
        <div className="flex-1 mx-3 bg-white/10 rounded-full px-3 py-1 text-xs text-gray-400">https://yourapp.com/api</div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-3">
        {/* API Route Cards */}
        {[[Code2, "GET", "/api/users", "text-green-400"], [Server, "POST", "/api/orders", "text-blue-400"], [Database, "PUT", "/api/products", "text-yellow-400"]].map(([Icon, method, route, color]) => (
          <div key={route} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
            <span className={`text-xs font-bold ${color}`}>{method}</span>
            <Icon size={14} className="text-gray-400" />
            <span className="text-gray-300 text-xs font-mono">{route}</span>
            <span className="ml-auto text-xs text-green-400">200</span>
          </div>
        ))}

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-2 pt-1">
          {[[Zap, "Fast"], [Shield, "Secure"], [Layers, "Scalable"]].map(([Icon, label]) => (
            <div key={label} className="flex flex-col items-center gap-1 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-xs">
              <Icon size={14} className="text-blue-400" />{label}
            </div>
          ))}
        </div>

        {/* Deploy Button */}
        <button className="w-full py-3 rounded-xl font-semibold text-white text-sm bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400 shadow-[0_0_20px_rgba(37,99,235,.4)] hover:scale-105 duration-300">
          Deploy API →
        </button>
      </div>
    </div>

    {/* Badges */}
    <div className="absolute top-16 right-4 px-4 py-2 rounded-full bg-gradient-to-r from-blue-700 to-indigo-500 text-white text-xs shadow-xl flex items-center gap-2">
      <CheckCircle className="text-green-400" size={14} /> REST & GraphQL
    </div>
    <div className="absolute bottom-24 left-2 px-4 py-2 rounded-full bg-cyan-500/20 border border-cyan-400/40 text-cyan-300 text-xs flex items-center gap-2">
      <Zap size={14} /> 99.9% Uptime
    </div>
    <div className="absolute bottom-40 right-2 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-400/40 text-blue-300 text-xs flex items-center gap-2">
      <Globe size={14} /> Global CDN
    </div>
  </div>
);

export default function WebServicePage() {
  return (
    <div className="min-h-screen bg-white overflow-x-clip">
      <Navbar />
      <ServiceHeroSection
        badge="Back-End Development"
        title={(
          <>
            <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
              Custom Back-End
            </span>
            <br /> Web Development
          </>
        )}
        description="We craft scalable APIs, data architectures, and server-side systems that power modern web products with speed, security, and reliability."
        media={<WebUI />}
      />
      <StatsCounter />
      <WebServiceCoreTech />
      <WebServiceCloudAndIntegrations />
      <WebServiceEnterpriseQuality />
      <CallToAction />
      <Footer />
    </div>
  );
}
